/**
 * The person's collaboration inbox (`GET /api/v1/me/inbox`, services/CollabService.js: mentions, shares, handoff
 * notes, answers to suggestions, agents' notes), shared by the notifications bell and the sidebar's Activity badge: one
 * live poll (utils/livePoll.js) for both, so a project shared with you or a mention shows without a reload. `inbox` is
 * null until the first answer, or where the gateway has no inbox (404).
 *
 * Items read here stay read over the answers until the gateway says so too (a poll that left before the click).
 */
import { getInbox, markInboxRead } from '../services/CollabService';
import { createLivePoll, useLivePoll } from '../utils/livePoll';

const readHere = new Set();

const unreadOf = (items) => items.filter((i) => !i.read).length;

function withLocal(answer) {
  if (!answer) { return null; }
  let flipped = 0;
  const items = (answer.items || []).map((item) => {
    if (!readHere.has(item.id)) { return item; }
    if (item.read) { readHere.delete(item.id); return item; }
    flipped += 1;
    return { ...item, read: true };
  });
  return { ...answer, items, unread: Math.max(0, (answer.unread ?? unreadOf(answer.items || [])) - flipped) };
}

const poll = createLivePoll({
  load: async() => withLocal(await getInbox({ limit: 50 })),
  onClear: () => readHere.clear(),
});

export function refreshInbox() {
  return poll.refresh();
}

/** Marks `ids` (or everything, `null`) read here at once and at the gateway. */
export function readInbox(ids) {
  const state = poll.getSnapshot();
  if (!state) { return Promise.resolve(); }
  const all = !ids;
  const items = (state.items || []).map((i) => {
    if (!(all || ids.includes(i.id)) || i.read) { return i; }
    readHere.add(i.id);
    return { ...i, read: true };
  });
  const before = state.unread ?? unreadOf(state.items || []);
  poll.set({ ...state, items, unread: all ? 0 : Math.max(0, before - (unreadOf(state.items || []) - unreadOf(items))) });
  return markInboxRead(all ? { all: true } : { ids }).catch(() => {});
}

/** For tests: forget everything. */
export function resetInbox() {
  poll.reset();
}

/** `{ inbox, refresh, read }`; `enabled` false: nothing is polled for this caller. */
export default function useInbox({ enabled = true } = {}) {
  const inbox = useLivePoll(poll, { enabled });
  return { inbox, refresh: refreshInbox, read: readInbox };
}
